import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { lbToDisplay, displayToLb, weightUnit, heightUnit, KG_PER_LB, CM_PER_IN } from "@/lib/units";
import { LogOut, Shield, ChevronRight, Ruler, LifeBuoy, Sun, Moon, Lock, Loader2 } from "lucide-react";

const round1 = (n) => Math.round(n * 10) / 10;

export default function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const activeTheme = theme === "light" || theme === "dark" ? theme : (resolvedTheme || "dark");
  const [units, setUnits] = useState(user?.units || "imperial");
  const [form, setForm] = useState({
    name: user?.name || "", age: user?.age || "",
    weight: user?.weight_lb ? round1(lbToDisplay(user.weight_lb, { units: user?.units || "imperial" })) : "",
    height: user?.height_in ? round1((user?.units === "metric" ? user.height_in * CM_PER_IN : user.height_in)) : "",
    goal: user?.goal || "", injuries: user?.injuries || "",
    nutrition_preferences: user?.nutrition_preferences || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Toggle units and convert the weight + height fields in place so the numbers stay correct.
  const changeUnits = async (next) => {
    if (!next || next === units) return;
    setForm((f) => {
      const out = { ...f };
      if (f.weight !== "" && f.weight !== null && !isNaN(parseFloat(f.weight))) {
        const w = parseFloat(f.weight);
        out.weight = round1(next === "metric" ? w * KG_PER_LB : w / KG_PER_LB);
      }
      if (f.height !== "" && f.height !== null && !isNaN(parseFloat(f.height))) {
        const h = parseFloat(f.height);
        out.height = round1(next === "metric" ? h * CM_PER_IN : h / CM_PER_IN);
      }
      return out;
    });
    setUnits(next);
    try {
      await updateProfile({ units: next });
      toast.success(`Units set to ${next === "metric" ? "Metric (kg · km)" : "Imperial (lb · mi)"}`);
    } catch (e) { toast.error("Could not update units"); }
  };

  const save = async () => {
    try {
      await updateProfile({
        name: form.name, age: form.age ? parseInt(form.age) : null,
        weight_lb: form.weight !== "" ? parseFloat(displayToLb(form.weight, { units }).toFixed(2)) : null,
        height_in: form.height !== "" ? parseFloat((units === "metric" ? parseFloat(form.height) / CM_PER_IN : parseFloat(form.height)).toFixed(2)) : null,
        goal: form.goal, injuries: form.injuries, nutrition_preferences: form.nutrition_preferences,
        units,
      });
      toast.success("Profile updated");
    } catch (e) { toast.error("Could not update"); }
  };

  const status = user?.access?.status;
  const [portalLoading, setPortalLoading] = useState(false);

  const openBilling = async () => {
    if (!user?.has_billing) {
      navigate("/subscribe");
      return;
    }
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  return (
    <AppShell title="Profile">
      <Card className="rounded-2xl bg-card border-border p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Subscription</p>
            <p className="font-display text-lg font-bold capitalize">{user?.plan === "admin" ? "Admin" : (user?.plan || "None")}</p>
            <Badge data-testid="subscription-status-badge" className="mt-1 bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20">{status}</Badge>
            {user?.subscription_cancel_at_period_end && (
              <p className="text-[11px] text-muted-foreground mt-1.5">Cancels at end of billing period</p>
            )}
          </div>
          {user?.role === "admin" ? (
            <Badge className="bg-[#ccff0c] text-black">Admin</Badge>
          ) : (
            <Button data-testid="manage-sub" onClick={openBilling} disabled={portalLoading} className="bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40">
              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (user?.has_billing ? "Manage" : "Subscribe")}
            </Button>
          )}
        </div>
      </Card>

      {user?.role === "admin" && (
        <Card onClick={() => navigate("/admin")} className="rounded-2xl bg-card border-border p-4 mb-4 cursor-pointer flex items-center justify-between">
          <div className="flex items-center gap-3"><Shield className="h-5 w-5 text-[#ccff0c]" /><span className="font-semibold">Admin Dashboard</span></div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </Card>
      )}

      {/* Units preference */}
      <Card className="rounded-2xl bg-card border-border p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Ruler className="h-4 w-4 text-[#ccff0c]" />
          <div>
            <p className="font-semibold text-sm">Units</p>
            <p className="text-[11px] text-muted-foreground">Applies to runs and weights (nutrition stays the same)</p>
          </div>
        </div>
        <ToggleGroup type="single" value={units} onValueChange={changeUnits} className="grid grid-cols-2 gap-2">
          <ToggleGroupItem data-testid="units-imperial" value="imperial" className="data-[state=on]:bg-[#ccff0c] data-[state=on]:text-black border border-border bg-muted h-11 rounded-xl">
            Imperial · lb · mi
          </ToggleGroupItem>
          <ToggleGroupItem data-testid="units-metric" value="metric" className="data-[state=on]:bg-[#ccff0c] data-[state=on]:text-black border border-border bg-muted h-11 rounded-xl">
            Metric · kg · km
          </ToggleGroupItem>
        </ToggleGroup>
      </Card>

      {/* Appearance / theme preference */}
      <Card className="rounded-2xl bg-card border-border p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Sun className="h-4 w-4 text-[#ccff0c]" />
          <div>
            <p className="font-semibold text-sm">Appearance</p>
            <p className="text-[11px] text-muted-foreground">Choose a light or dark interface</p>
          </div>
        </div>
        <ToggleGroup type="single" value={activeTheme} onValueChange={(v) => v && setTheme(v)} className="grid grid-cols-2 gap-2">
          <ToggleGroupItem data-testid="theme-dark" value="dark" className="data-[state=on]:bg-[#ccff0c] data-[state=on]:text-black border border-border bg-muted h-11 rounded-xl gap-2">
            <Moon className="h-4 w-4" /> Dark
          </ToggleGroupItem>
          <ToggleGroupItem data-testid="theme-light" value="light" className="data-[state=on]:bg-[#ccff0c] data-[state=on]:text-black border border-border bg-muted h-11 rounded-xl gap-2">
            <Sun className="h-4 w-4" /> Light
          </ToggleGroupItem>
        </ToggleGroup>
      </Card>

      <Card className="rounded-2xl bg-card border-border p-4 space-y-3">
        <div><Label className="text-xs">Name</Label><Input data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-card border-border h-10" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Age</Label><Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-card border-border h-10" /></div>
          <div><Label className="text-xs">Height ({heightUnit({ units })})</Label><Input data-testid="profile-height" type="number" value={form.height} onChange={(e) => set("height", e.target.value)} className="bg-card border-border h-10" /></div>
          <div><Label className="text-xs">Weight ({weightUnit({ units })})</Label><Input data-testid="profile-weight" type="number" value={form.weight} onChange={(e) => set("weight", e.target.value)} className="bg-card border-border h-10" /></div>
        </div>
        <div><Label className="text-xs">Injuries / limitations</Label><Textarea value={form.injuries} onChange={(e) => set("injuries", e.target.value)} className="bg-card border-border" /></div>
        <div><Label className="text-xs">Nutrition preferences</Label><Textarea value={form.nutrition_preferences} onChange={(e) => set("nutrition_preferences", e.target.value)} className="bg-card border-border" /></div>
        <Button data-testid="profile-save" onClick={save} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Save changes</Button>
      </Card>

      {/* Support & feedback */}
      <Card data-testid="support-link" onClick={() => navigate("/support")} className="rounded-2xl bg-card border-border p-4 mt-4 cursor-pointer flex items-center justify-between hover:border-[#ccff0c]/40 transition-colors">
        <div className="flex items-center gap-3"><LifeBuoy className="h-5 w-5 text-[#ccff0c]" /><span className="font-semibold">Support &amp; Feedback</span></div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </Card>

      {/* Privacy policy */}
      <Card data-testid="privacy-link" onClick={() => navigate("/privacy")} className="rounded-2xl bg-card border-border p-4 mt-3 cursor-pointer flex items-center justify-between hover:border-[#ccff0c]/40 transition-colors">
        <div className="flex items-center gap-3"><Lock className="h-5 w-5 text-[#ccff0c]" /><span className="font-semibold">Privacy Policy</span></div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </Card>

      <Button data-testid="logout" onClick={() => { logout(); navigate("/auth"); }} className="w-full mt-4 bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20">
        <LogOut className="h-4 w-4 mr-2" /> Log out
      </Button>
    </AppShell>
  );
}
